import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close,
} from "@mui/icons-material";

// Schedule Dialog Component
const ScheduleDialog = ({
  open,
  onClose,
  scheduleData,
  onScheduleChange,
  onSave,
  tabValue,
  isLoading,
}) => {
  const { t } = useTranslation("dashboard");
  // Helper function to convert 12-hour format to 24-hour format for HTML time inputs
  const convertTo24Hour = (time12h) => {
    if (!time12h) return '';
    
    // Handle cases where time might already be in 24-hour format
    if (time12h.includes(':')) {
      const parts = time12h.split(' ');
      if (parts.length === 1) {
        // Already in 24-hour format (HH:MM)
        return time12h;
      }
      
      // Convert from 12-hour format (HH:MM AM/PM)
      const [time, modifier] = parts;
      let [hours, minutes] = time.split(':');
      
      hours = parseInt(hours, 10);
      
      if (hours === 12) {
        hours = modifier === 'PM' ? 12 : 0;
      } else if (modifier === 'PM') {
        hours = hours + 12;
      }
      
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    
    return '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        color: 'primary.main',
        fontWeight: 600,
        fontSize: '1.25rem'
      }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
          {tabValue === 0 ? t("menuSchedule") : t("catalogSchedule")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: 'black' }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {scheduleData.map((schedule, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
                borderBottom: index < scheduleData.length - 1 ? '1px solid #f0f0f0' : 'none',
              }}
            >
              {/* Day Name */}
              <Typography
                sx={{
                  fontWeight: 500,
                  color: '#333',
                  minWidth: 80,
                  textTransform: 'lowercase',
                }}
              >
                {schedule.day}
              </Typography>

              {/* Time Inputs */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: 'center' }}>
                <TextField
                  type="time"
                  value={convertTo24Hour(schedule.openTime)}
                  onChange={(e) => {
                    onScheduleChange(index, 'openTime', e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: '#f8f9fa',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#bdbdbd',
                      },
                    },
                  }}
                  InputProps={{
                    style: { fontSize: '0.875rem' }
                  }}
                />
                <Typography sx={{ color: '#666' }}>{t("toLabel")}</Typography>
                <TextField
                  type="time"
                  value={convertTo24Hour(schedule.closeTime)}
                  onChange={(e) => {
                    onScheduleChange(index, 'closeTime', e.target.value);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      bgcolor: '#f8f9fa',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#bdbdbd',
                      },
                    },
                  }}
                  InputProps={{
                    style: { fontSize: '0.875rem' }
                  }}
                />
              </Box>

              {/* Checkbox */}
              <FormControlLabel
                control={
                  <Switch
                    checked={schedule.isEnabled || false}
                    onChange={(e) => onScheduleChange(index, 'isEnabled', e.target.checked)}
                    color="primary"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  />
                }
                label=""
                sx={{ ml: 0 }}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            fontSize: '1rem',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&:disabled': {
              bgcolor: '#ccc',
              color: '#666',
            },
          }}
        >
          {isLoading ? t("saving") : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;